<script setup>
import { computed, onBeforeUnmount, ref, shallowRef } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiAuthLayout from '@/components/ui/UiAuthLayout.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import UiOtpInput from '@/components/ui/UiOtpInput.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { email, matches, strongPassword } from '@/utils/validation';

// Forgot password: email → code sent to that email → new password.
const router = useRouter();
const store = useStore();

const step = shallowRef('email'); // 'email' | 'code' | 'password' | 'done'
const loading = shallowRef(false);
const error = shallowRef('');

const emailRef = ref(null);
const emailForm = ref({ email: '' });
const emailFields = [{ key: 'email', label: 'Email address', required: true, rules: [email], placeholder: 'email@example.com' }];

const code = shallowRef('');

const passwordRef = ref(null);
const passwordForm = ref({ password: '', confirm: '' });
const passwordFields = [
  {
    key: 'password',
    label: 'New password',
    type: 'password',
    required: true,
    rules: [strongPassword],
    description: 'At least 8 characters with upper and lower case letters, a number and a symbol.',
  },
  { key: 'confirm', label: 'Confirm password', type: 'password', required: true, rules: [matches(() => passwordForm.value.password, 'Passwords do not match.')] },
];

async function attempt(request, fallback) {
  loading.value = true;
  error.value = '';
  try {
    await request();
    return true;
  } catch (e) {
    error.value = errorMessage(e, fallback);
    return false;
  } finally {
    loading.value = false;
  }
}

// The 15-minute token from a confirmed code; kept in memory only.
const resetToken = shallowRef('');
const resendIn = shallowRef(0);
let resendTimer = null;

function startResendCooldown() {
  resendIn.value = 60;
  clearInterval(resendTimer);
  resendTimer = setInterval(() => {
    resendIn.value -= 1;
    if (resendIn.value <= 0) clearInterval(resendTimer);
  }, 1000);
}
onBeforeUnmount(() => clearInterval(resendTimer));

async function requestCode() {
  const address = emailForm.value.email.trim();
  const ok = await attempt(
    async () => assertOk(await apiRequest('post', '/auth/forgotPassword', { email: address }), [200]),
    'Could not send the code.',
  );
  if (ok) {
    store.dispatch('setEmail', address);
    code.value = '';
    startResendCooldown();
  }
  return ok;
}

async function sendCode() {
  const { valid } = await emailRef.value.validate();
  if (valid && (await requestCode())) step.value = 'code';
}

async function checkCode() {
  if (code.value.length !== 6) {
    error.value = 'Enter the 6-digit code from your email.';
    return;
  }
  const ok = await attempt(async () => {
    const body = assertOk(
      await apiRequest('post', '/auth/verifyResetCode', { email: store.getters.getEmail, otp: code.value }),
      [200],
    );
    resetToken.value = body.resetToken;
  }, 'That code didn’t work. Check it and try again.');
  if (ok) step.value = 'password';
}

async function savePassword() {
  const { valid } = await passwordRef.value.validate();
  if (!valid) return;
  const ok = await attempt(
    async () =>
      assertOk(
        await apiRequest('post', '/auth/resetPassword', {
          resetToken: resetToken.value,
          password: passwordForm.value.password,
        }),
        [200],
      ),
    'Could not change your password.',
  );
  if (ok) {
    resetToken.value = '';
    store.dispatch('setEmail', null);
    step.value = 'done';
  }
}

const header = computed(
  () =>
    ({
      email: { title: 'Forgot password', description: 'Enter your email and we’ll send you a code to reset your password.' },
      code: {
        title: 'Check your email',
        description: `We sent a 6-digit code to ${emailForm.value.email}. It can take a minute to arrive.`,
      },
      password: { title: 'Choose a new password', description: 'Use a password you don’t use anywhere else.' },
      done: { title: 'Password changed', description: 'You can now sign in with your new password.' },
    })[step.value],
);
</script>

<template>
  <UiAuthLayout :title="header.title" :description="header.description">
    <div class="form-stack">
      <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

      <v-form v-if="step === 'email'" ref="emailRef" class="form-stack" @submit.prevent="sendCode">
        <UiFormFields v-model:values="emailForm" :fields="emailFields" />
        <v-btn type="submit" color="primary" block :loading="loading">Email me a code</v-btn>
      </v-form>

      <form v-else-if="step === 'code'" class="form-stack" @submit.prevent="checkCode">
        <UiOtpInput v-model="code" :disabled="loading" @complete="checkCode" />
        <v-btn type="submit" color="primary" block :loading="loading">Continue</v-btn>
        <p class="resend">
          Didn’t get it?
          <button v-if="resendIn <= 0" type="button" class="link" :disabled="loading" @click="requestCode">Resend code</button>
          <span v-else>Resend in {{ resendIn }}s</span>
          · <button type="button" class="link" :disabled="loading" @click="step = 'email'">Use a different email</button>
        </p>
      </form>

      <v-form v-else-if="step === 'password'" ref="passwordRef" class="form-stack" @submit.prevent="savePassword">
        <UiFormFields v-model:values="passwordForm" :fields="passwordFields" />
        <v-btn type="submit" color="primary" block :loading="loading">Change password</v-btn>
      </v-form>

      <v-btn v-else color="primary" block @click="router.push('/login')">Go to sign in</v-btn>
    </div>

    <template v-if="step !== 'done'" #footer>
      Remembered it? <router-link to="/login">Back to sign in</router-link>
    </template>
  </UiAuthLayout>
</template>

<style scoped>
.resend {
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
.link {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
}
</style>
